// hooks/useCategoryData.ts
import { useState, useCallback } from 'react';
import { CategoryItem, CategoryStructure, CategoryResetResponse, ApiResponse } from '../types';

export const useCategoryData = () => {
  const [categoryList, setCategoryList] = useState<CategoryItem[]>([]);
  const [categories, setCategories] = useState<CategoryStructure>({});

  const parseCategoryPayload = (payload: unknown): CategoryItem[] => {
    if (Array.isArray(payload)) return payload as CategoryItem[];
    if (
      payload &&
      typeof payload === 'object' &&
      'status' in payload &&
      'data' in payload &&
      (payload as ApiResponse<CategoryItem[]>).status
    ) {
      return (payload as ApiResponse<CategoryItem[]>).data || [];
    }
    return [];
  };

  const fetchCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/vendor/category', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('카테고리 조회 실패');
      const payload = await response.json();
      const rawCategories = parseCategoryPayload(payload);

      const sortedCategories = rawCategories
        .map((parent) => ({
          ...parent,
          children: Array.isArray(parent.children)
            ? [...parent.children].sort(
                (a, b) => (a.orderNo ?? Number.MAX_SAFE_INTEGER) - (b.orderNo ?? Number.MAX_SAFE_INTEGER)
              )
            : []
        }))
        .sort((a, b) => (a.orderNo ?? Number.MAX_SAFE_INTEGER) - (b.orderNo ?? Number.MAX_SAFE_INTEGER));

      setCategoryList(sortedCategories);

      const structure: CategoryStructure = {};
      sortedCategories.forEach((cat) => {
        structure[cat.name] = cat.children?.map((sub) => sub.name) || [];
      });
      setCategories(structure);
    } catch (error) {
      console.error('Fetch categories error:', error);
    }
  }, []);

  const resetCategories = async (): Promise<CategoryResetResponse | null> => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/vendor/category/reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const payload = await response.json();
      const data =
        payload && typeof payload === 'object' && 'data' in payload
          ? (payload as { data?: CategoryResetResponse }).data
          : (payload as CategoryResetResponse);
      const message =
        payload && typeof payload === 'object' && 'message' in payload
          ? String((payload as { message?: string }).message || '')
          : '';

      if (!response.ok) throw new Error(message || '카테고리 초기화 실패');
      return data || null;
    } catch (error) {
      console.error('Reset category error:', error);
      return null;
    }
  };

  return { categoryList, categories, fetchCategories, resetCategories };
};
